/**
 * Sticky undo toast for the fast dose-entry screen.
 *
 * Saving happens on a single tap with no confirmation step, so the toast IS
 * the safety net: it names what was recorded and offers Annuler for 7 seconds.
 * role="status" so a screen reader announces the save without stealing focus.
 */
import { useEffect } from 'react';
import { useI18n } from '../lib/i18n.js';

export interface ToastState {
  message: string;
  tone: 'ok' | 'error';
  onUndo?: () => void;
}

/** How long a save stays undoable, per the spec. */
export const TOAST_MS = 7000;

export function UndoToast({ toast, onDismiss }: { toast: ToastState | null; onDismiss: () => void }) {
  const { t } = useI18n();

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onDismiss, TOAST_MS);
    return () => clearTimeout(id);
    // A NEW save replaces the current toast, so the timer restarts with it.
  }, [toast, onDismiss]);

  if (!toast) return null;

  return (
    <div
      role="status"
      className={`sticky bottom-4 z-10 mt-4 flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 text-[15px] text-white shadow-lg ${
        toast.tone === 'error' ? 'bg-danger' : 'bg-[#17212B]'
      }`}
    >
      <span className="min-w-0">{toast.message}</span>
      {toast.onUndo && (
        <button
          type="button"
          onClick={toast.onUndo}
          className="min-h-[44px] shrink-0 rounded-lg px-3.5 text-[15px] font-bold text-[#9FD6E6]"
        >
          {t('entry.undo')}
        </button>
      )}
    </div>
  );
}
